import React, { useState, useEffect } from 'react';
import CustomerLayout from '../components/CustomerLayout';
import { customerAPI, authAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import '../App.css';

export default function Profile() {
    const { user, updateUser } = useAuth();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    
    // Form states
    const [contactInfo, setContactInfo] = useState({
        name: '',
        contactName: '',
        contactEmail: '',
        contactPhone: ''
    });
    
    const [address, setAddress] = useState({
        address: '',
        city: '',
        state: '',
        postalCode: '',
        country: 'Australia'
    });
    
    const [passwordChange, setPasswordChange] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [changingPassword, setChangingPassword] = useState(false);

    useEffect(() => {
        fetchProfile();
    }, []);

    const fetchProfile = async () => {
        setLoading(true);
       setError(null);
        try {
            const response = await customerAPI.getProfile();
            const data = response.data;
            setProfile(data);
            
            setContactInfo({
                name: data.customer_name || '',
                contactName: data.contact_name || '',
                contactEmail: data.contact_email || '',
                contactPhone: data.contact_phone || ''
            });
            
            setAddress({
                address: data.address || '',
                city: data.city || '',
                state: data.state || '',
                postalCode: data.postal_code || '',
                country: data.country || 'Australia'
            });
        } catch (err) {
            console.error('Error fetching profile:', err);
            setError(err.response?.data?.error || 'Failed to load profile');
        } finally {
            setLoading(false);
        }
    };

    const saveProfile = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        setSuccess(false);
        
        try {
            await customerAPI.updateProfile({
                ...contactInfo,
                ...address
            });
            
            // Update auth context with customer name
            updateUser({
                customerName: contactInfo.name
            });
            
            setSuccess(true);
            setTimeout(() => setSuccess(false), 3000);
        } catch (err) {
            console.error('Error updating profile:', err);
            setError(err.response?.data?.error || 'Failed to update profile');
        } finally {
            setSaving(false);
        }
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        
        if (passwordChange.newPassword !== passwordChange.confirmPassword) {
            alert('New passwords do not match');
            return;
        }
        
        if (passwordChange.newPassword.length < 8) {
            alert('Password must be at least 8 characters long');
            return;
        }
        
        setChangingPassword(true);
        setError(null);
        
        try {
            await authAPI.changePassword(
                passwordChange.currentPassword,
                passwordChange.newPassword
            );
            
            alert('Password changed successfully');
            setPasswordChange({
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            });
        } catch (err) {
            console.error('Error changing password:', err);
            alert(err.response?.data?.error || 'Failed to change password');
        } finally {
            setChangingPassword(false);
        }
    };

    if (loading) {
        return (
            <CustomerLayout>
                <div style={{ textAlign: 'center', padding: '60px' }}>
                    <div className="spinner"></div>
                    <p style={{ marginTop: '20px', color: '#666' }}>Loading profile...</p>
                </div>
            </CustomerLayout>
        );
    }

    return (
        <CustomerLayout>
            <div style={{ maxWidth: '800px' }}>
                <h1 style={{ marginBottom: '30px' }}>My Profile</h1>

                {error && (
                    <div className="alert alert-error" style={{ marginBottom: '20px' }}>{error}</div>
                )}
                
                {success && (
                    <div className="alert alert-success" style={{ marginBottom: '20px' }}>
                        Profile updated successfully!
                    </div>
                )}

                {/* Contact Information */}
                <div className="card" style={{ marginBottom: '20px' }}>
                    <h2 style={{ marginBottom: '20px' }}>Customer Information</h2>
                    <form onSubmit={saveProfile}>
                        <div className="form-group" style={{ marginBottom: '20px' }}>
                            <label className="form-label">Company/Customer Name</label>
                            <input
                                type="text"
                                className="form-input"
                                value={contactInfo.name}
                                onChange={(e) => setContactInfo({ ...contactInfo, name: e.target.value })}
                                placeholder="Company or customer name"
                            />
                        </div>

                        <div className="form-group" style={{ marginBottom: '20px' }}>
                            <label className="form-label">Primary Contact Name</label>
                            <input
                                type="text"
                                className="form-input"
                                value={contactInfo.contactName}
                                onChange={(e) => setContactInfo({ ...contactInfo, contactName: e.target.value })}
                                placeholder="Contact person name"
                            />
                        </div>

                        <div className="form-group">
                            <label className="form-label">Portal Email (Read Only)</label>
                            <input
                                type="email"
                                className="form-input"
                                value={user?.email || 'Loading...'}
                                disabled
                                style={{ background: '#f5f5f5' }}
                            />
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                            <div className="form-group">
                                <label className="form-label">Contact Email</label>
                                <input
                                    type="email"
                                    className="form-input"
                                    value={contactInfo.contactEmail}
                                    onChange={(e) => setContactInfo({ ...contactInfo, contactEmail: e.target.value })}
                                    placeholder="Contact email address"
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Contact Phone</label>
                                <input
                                    type="tel"
                                    className="form-input"
                                    value={contactInfo.contactPhone}
                                    onChange={(e) => setContactInfo({ ...contactInfo, contactPhone: e.target.value })}
                                    placeholder="Contact phone number"
                                />
                            </div>
                        </div>

                        <h3 style={{ marginTop: '30px', marginBottom: '20px' }}>Address</h3>

                        <div className="form-group">
                            <label className="form-label">Street Address</label>
                            <input
                                type="text"
                                className="form-input"
                                value={address.address}
                                onChange={(e) => setAddress({ ...address, address: e.target.value })}
                            />
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                            <div className="form-group">
                                <label className="form-label">City</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={address.city}
                                    onChange={(e) => setAddress({ ...address, city: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">State</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={address.state}
                                    onChange={(e) => setAddress({ ...address, state: e.target.value })}
                                />
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                            <div className="form-group">
                                <label className="form-label">Postcode</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={address.postalCode}
                                    onChange={(e) => setAddress({ ...address, postalCode: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Country</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={address.country}
                                    onChange={(e) => setAddress({ ...address, country: e.target.value })}
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={saving}
                            style={{ marginTop: '20px' }}
                        >
                            {saving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </form>
                </div>

                {/* Change Password */}
                <div className="card">
                    <h2 style={{ marginBottom: '20px' }}>Change Password</h2>
                    <form onSubmit={handlePasswordChange}>
                        <div className="form-group">
                            <label className="form-label">Current Password</label>
                            <input
                                type="password"
                                className="form-input"
                                value={passwordChange.currentPassword}
                                onChange={(e) => setPasswordChange({ ...passwordChange, currentPassword: e.target.value })}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label className="form-label">New Password</label>
                            <input
                                type="password"
                                className="form-input"
                                value={passwordChange.newPassword}
                                onChange={(e) => setPasswordChange({ ...passwordChange, newPassword: e.target.value })}
                                required
                                minLength="8"
                            />
                            <small style={{ color: '#666', fontSize: '12px' }}>
                                Minimum 8 characters
                            </small>
                        </div>

                        <div className="form-group">
                            <label className="form-label">Confirm New Password</label>
                            <input
                                type="password"
                                className="form-input"
                                value={passwordChange.confirmPassword}
                                onChange={(e) => setPasswordChange({ ...passwordChange, confirmPassword: e.target.value })}
                                required
                            />
                        </div>

                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={changingPassword}
                        >
                            {changingPassword ? 'Changing Password...' : 'Change Password'}
                        </button>
                    </form>
                </div>
            </div>
        </CustomerLayout>
    );
}
